import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ProductsService } from '../../services/products.service';
import { Product } from '../../models/Product';
import { ErrorService } from '../../shared/services/error.service';
import { TableAction } from '../../shared/components/table/table.component';

@Component({
  selector: 'app-products',
  templateUrl: './products.component.html',
  styleUrls: ['./products.component.css']
})
export class ProductsComponent implements OnInit {
  products: Product[] = [];
  isLoading = false;
  selectedType = '';
  selectedStatus = '';

  // Configuração da tabela genérica
  columns = [
    { titulo: 'Código', label: 'code' },
    { titulo: 'Nome', label: 'name' },
    { titulo: 'Tipo', label: 'type', formatter: (value: string) => this.getProductTypeLabel(value) },
    { titulo: 'Ciclo', label: 'billingCycleDisplay' },
    { titulo: 'Duração', label: 'daysDisplay' },
    { titulo: 'Preço', label: 'basePrice', formatter: (value: number) => this.formatPrice(value) },
    { titulo: 'Status', label: 'isActive', formatter: (value: boolean) => value ? 'Ativo' : 'Inativo' }
  ];

  actions: TableAction[] = [
    {
      label: 'Visualizar',
      icon: 'fa-solid fa-eye',
      action: 'view',
      class: 'btn-view',
      visible: true
    },
    {
      label: 'Editar',
      icon: 'fa-solid fa-edit',
      action: 'edit',
      class: 'btn-edit',
      visible: true
    },
    {
      label: 'Ativar/Desativar',
      icon: 'fa-solid fa-toggle-on',
      action: 'toggle',
      class: 'btn-toggle',
      visible: false
    },
    {
      label: 'Excluir',
      icon: 'fa-solid fa-trash',
      action: 'delete',
      class: 'btn-delete',
      visible: false
    }
  ];

  // Opções para filtros
  productTypes = [
    { value: '', label: 'Todos os tipos' },
    { value: 'Plan', label: 'Plano' },
    { value: 'Service', label: 'Serviço' },
    { value: 'Product', label: 'Produto' },
    { value: 'Subscription', label: 'Assinatura' }
  ];
  statusOptions = [
    { value: '', label: 'Todos' },
    { value: 'true', label: 'Ativos' },
    { value: 'false', label: 'Inativos' }
  ];

  constructor(
    private router: Router,
    private productsService: ProductsService,
    private messageService: ErrorService
  ) {}

  ngOnInit(): void {
    this.loadProducts();
  }

  loadProducts(): void {
    this.isLoading = true;
    this.productsService.getProducts().subscribe({
      next: (products) => {
        this.products = products.map(product => ({
          ...product,
          billingCycleDisplay: this.getBillingCycleDisplay(product),
          daysDisplay: this.getDaysDisplay(product)
        })) as Product[];
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar produtos:', error);
        this.isLoading = false;
        this.messageService.showErrorMessage('Erro ao carregar produtos. Tente novamente.');
      }
    });
  }

  navigateToNewProduct(): void {
    this.router.navigate(['/products/new']);
  }

  navigateToEditProduct(product: Product): void {
    const productId = this.getValidProductId(product);
    if (!productId) {
      this.messageService.showErrorMessage('Produto sem identificador válido para edição.');
      return;
    }
    this.router.navigate(['/products', productId, 'edit']);
  }

  navigateToViewProduct(product: Product): void {
    const productId = this.getValidProductId(product);
    if (!productId) {
      this.messageService.showErrorMessage('Produto sem identificador válido para visualização.');
      return;
    }
    this.router.navigate(['/products', productId, 'view']);
  }

  deleteProduct(product: Product): void {
    const productId = this.getValidProductId(product);
    if (!productId) {
      this.messageService.showErrorMessage('Produto sem identificador válido para exclusão.');
      return;
    }
    if (confirm(`Tem certeza que deseja excluir o produto "${product.name}"?`)) {
      this.isLoading = true;
      
      this.productsService.deleteProduct(productId).subscribe({
        next: () => {
          this.loadProducts(); // Recarregar lista
          this.messageService.showInfoMessage('Produto excluído com sucesso!');
        },
        error: (error) => {
          console.error('Erro ao excluir produto:', error);
          this.isLoading = false;
          this.messageService.showErrorMessage('Erro ao excluir produto. Tente novamente.');
        }
      });
    }
  }

  toggleProductStatus(product: Product): void {
    const productId = this.getValidProductId(product);
    if (!productId) {
      this.messageService.showErrorMessage('Produto sem identificador válido para alterar status.');
      return;
    }

    const newStatus = !product.isActive;
    const updatedProduct = { ...product, isActive: newStatus };
    
    this.productsService.updateProduct(productId, updatedProduct).subscribe({
      next: (updated) => {
        const index = this.products.findIndex(p => String(this.getValidProductId(p)) === String(productId));
        if (index > -1) {
          this.products[index] = updated;
        }
        this.messageService.showInfoMessage(
          `Produto ${newStatus ? 'ativado' : 'desativado'} com sucesso!`
        );
      },
      error: (error) => {
        console.error('Erro ao alterar status do produto:', error);
        this.messageService.showErrorMessage('Erro ao alterar status. Tente novamente.');
      }
    });
  }

  get filteredProducts(): Product[] {
    // Aplicar apenas filtros de tipo e status
    // A busca será feita pela tabela genérica internamente
    return this.products.filter(product => {
      const matchesType = !this.selectedType || product.type === this.selectedType;
      const matchesStatus = !this.selectedStatus || 
        product.isActive.toString() === this.selectedStatus;
      
      return matchesType && matchesStatus;
    });
  }

  private getValidProductId(product: any): string | number | null {
    const raw = product?.id
      ?? product?.Id
      ?? product?.ID
      ?? product?.productId
      ?? product?.ProductId
      ?? product?.productID
      ?? product?.ProductID;

    if (raw === null || raw === undefined) {
      return null;
    }

    if (typeof raw === 'number') {
      return Number.isFinite(raw) && raw > 0 ? raw : null;
    }

    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      if (!trimmed) {
        return null;
      }
      return trimmed;
    }

    return null;
  }

  onSortChange(event: { sortBy: string; sortOrder: 'asc' | 'desc' }): void {
    this.products = [...this.products].sort((a, b) => {
      const va = String((a as any)[event.sortBy] ?? '');
      const vb = String((b as any)[event.sortBy] ?? '');
      const cmp = va.localeCompare(vb, 'pt-BR', { numeric: true });
      return event.sortOrder === 'asc' ? cmp : -cmp;
    });
  }

  onActionClick(event: {action: string, item: Product}): void {
    switch(event.action) {
      case 'view':
        this.navigateToViewProduct(event.item);
        break;
      case 'edit':
        this.navigateToEditProduct(event.item);
        break;
      case 'toggle':
        this.toggleProductStatus(event.item);
        break;
      case 'delete':
        this.deleteProduct(event.item);
        break;
    }
  }

  get activeProductsCount(): number {
    return this.products.filter(p => p.isActive).length;
  }

  get inactiveProductsCount(): number {
    return this.products.filter(p => !p.isActive).length;
  }

  get totalProductsCount(): number {
    return this.products.length;
  }

  formatPrice(price: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  }

  formatDays(days: number): string {
    if (days === 1) return '1 dia';
    return `${days} dias`;
  }

  isRecurringProduct(type: string): boolean {
    return type === 'Plan' || type === 'Subscription';
  }

  getBillingCycleDisplay(product: Product): string {
    if (!this.isRecurringProduct(product.type)) {
      return 'Por Unidade';
    }
    return product.billingCycle || '-';
  }

  getDaysDisplay(product: Product): string {
    if (!this.isRecurringProduct(product.type)) {
      return '-';
    }
    return this.formatDays(product.days || 0);
  }

  getProductTypeLabel(type: string): string {
    const typeOption = this.productTypes.find(t => t.value === type);
    return typeOption ? typeOption.label : type;
  }

}
